import React, { useCallback } from 'react';

import { IconProps, InputProps } from '@rbilabs/universal-components';

import AutocompleteOptionsDropdown from 'components/ucl/autocomplete-options-dropdown';
import useLocationPredictions from 'hooks/geolocation/use-location-predictions';
import useLocationPredictionsDelivery from 'hooks/geolocation/use-location-predictions-delivery';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { IPlaceData } from 'utils/geolocation';
import noop from 'utils/noop';

type BaseInputProps = Pick<InputProps, 'value'>;

export interface IAutocompleteAddressInputProps extends BaseInputProps {
  testID: string;
  autoFocus?: boolean;
  label: string;
  labelColor?: string;
  addressesOnly?: boolean;
  useDeliveryPredictions?: boolean;
  required: boolean;

  message?: string;
  setMessage?: React.Dispatch<React.SetStateAction<string>>;

  onAddressSelected: (address: string, placeId?: string, placeData?: IPlaceData) => void;
  onInputValueChange: (value: string) => void;
  onBlur?(): void;
  inputCustomProps?: InputProps;
  rightIconCustomProps?: IconProps;
}

export const AutocompleteAddressInput = React.forwardRef<
  HTMLInputElement,
  IAutocompleteAddressInputProps
>(
  (
    {
      testID,
      label,
      labelColor,
      message,
      addressesOnly = false,
      useDeliveryPredictions = false,
      onAddressSelected = noop,
      onInputValueChange,
      onBlur,
      setMessage,
      value = '',
      inputCustomProps,
      rightIconCustomProps,
    },
    ref
  ) => {
    /**
     * Retrieve predictions from the desired source and only pass the first five results.
     */
    const enableDoordashSuggestions =
      useFlag(LaunchDarklyFlag.ENABLE_DOORDASH_DRIVE_SUGGESTIONS) && useDeliveryPredictions;

    const deliveryPredictions = useLocationPredictionsDelivery(value, {
      enabled: enableDoordashSuggestions,
    });

    const defaultPredictions = useLocationPredictions(value, {
      addressesOnly,
      enabled: !enableDoordashSuggestions,
    });

    const predictions = (enableDoordashSuggestions
      ? deliveryPredictions
      : defaultPredictions
    )?.slice(0, 5);

    const handleDropdownValueSelected = useCallback(
      ({ address, placeId, placeData }: any) => {
        onAddressSelected(address, placeId, placeData);
        logEvent(CustomEventNames.MANUAL_LOCATION_SEARCH, EventTypes.Search);
      },
      [onAddressSelected]
    );

    const handleInputValueChange = useCallback(
      (inputValue: string | undefined) => {
        onInputValueChange(inputValue || '');

        if (setMessage) {
          setMessage('');
        }
      },
      [onInputValueChange, setMessage]
    );

    return (
      <AutocompleteOptionsDropdown
        noPredictionsFoundText={t('noLocationsMatchYourSearch')}
        testID={testID}
        onSelection={handleDropdownValueSelected}
        predictions={predictions}
        onInputChange={handleInputValueChange}
        onBlur={onBlur}
        value={value}
        label={label}
        labelColor={labelColor}
        errorMessage={message}
        ref={ref}
        clearTextIconTitle={t('clearText')}
        inputCustomProps={inputCustomProps}
        rightIconCustomProps={rightIconCustomProps}
      />
    );
  }
);

export default AutocompleteAddressInput;
